import { useState, useEffect, useCallback, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Fade from "embla-carousel-fade";
import { supabase } from "@/integrations/supabase/tenantClient";

interface CarouselSlide {
  id: string;
  image_url: string;
  display_order: number;
}

// Helper to detect media type from URL
const getMediaType = (url: string): 'image' | 'gif' | 'video' => {
  const extension = url.split('.').pop()?.toLowerCase();
  if (extension === 'mp4' || extension === 'webm') return 'video';
  if (extension === 'gif') return 'gif';
  return 'image';
};

export function AuthCarousel() {
  const [slides, setSlides] = useState<CarouselSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [_isVideoPlaying, _setIsVideoPlaying] = useState(false);
  const autoAdvanceRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, duration: 30 },
    [Fade()]
  );

  // Fetch active slides from database
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const { data, error } = await supabase
          .from("login_carousel_slides")
          .select("id, image_url, display_order")
          .eq("is_active", true)
          .order("display_order", { ascending: true });

        if (error) throw error;
        setSlides(data || []);
      } catch (error) {
        console.error("Error fetching carousel slides:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, []);

  // Check if current slide is a video
  const currentSlideIsVideo = slides[selectedIndex] 
    ? getMediaType(slides[selectedIndex].image_url) === 'video' 
    : false;

  // Auto-advance every 3 seconds (paused for videos)
  useEffect(() => {
    if (!emblaApi || slides.length <= 1) return;

    // Clear any existing interval
    if (autoAdvanceRef.current) {
      clearInterval(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }

    // Don't auto-advance if current slide is a video
    if (currentSlideIsVideo) return;

    autoAdvanceRef.current = setInterval(() => {
      emblaApi.scrollNext();
    }, 3000);

    return () => {
      if (autoAdvanceRef.current) {
        clearInterval(autoAdvanceRef.current);
      }
    };
  }, [emblaApi, slides.length, currentSlideIsVideo, selectedIndex]);

  // Track selected slide for indicators
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Loading state
  if (loading) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-background via-muted/20 to-background" />
    );
  }

  // No slides - show fallback gradient
  if (slides.length === 0) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_50%,hsl(var(--primary)/0.1),transparent_70%)]" />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Carousel container */}
      <div className="h-full" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, index) => {
            const mediaType = getMediaType(slide.image_url);
            const isActive = index === selectedIndex;
            
            return (
              <div
                key={slide.id}
                className="relative flex-[0_0_100%] min-w-0 h-full"
              >
                {mediaType === 'video' ? (
                  <video
                    src={slide.image_url}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay={isActive}
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img
                    src={slide.image_url}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                {/* Subtle overlay for better blending with dark theme */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background/20" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Dash indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((slide, index) => {
            const isVideo = getMediaType(slide.image_url) === 'video';
            return (
              <button
                key={index}
                onClick={() => emblaApi?.scrollTo(index)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === selectedIndex
                    ? `w-8 ${isVideo ? 'bg-primary' : 'bg-muted-foreground'}`
                    : "w-6 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
